import { OneWayDragger } from "./OneWayDragger";
import { DraggerType, type Point2D } from "./types";
import { PREVIEW_COMPLETION_STYLE } from "./previewStyles";

/**
 * Dragger for zooming to a window area.
 * User draws a rectangle, and the view zooms to that area.
 */
export class ZoomWindowDragger extends OneWayDragger {
  readonly type = DraggerType.ZoomWindow;

  private rectPreviewId: string | null = null;

  /** Callback to execute after zoom completes (e.g., switch back to previous tool) */
  private onZoomComplete: (() => void) | null = null;

  /**
   * Set a callback to be called after zoom completes.
   */
  setOnZoomComplete(callback: (() => void) | null): void {
    this.onZoomComplete = callback;
  }

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    // Create zoom window preview with distinctive style
    this.rectPreviewId = overlay.createRectangle(
      screen.x,
      screen.y,
      0,
      0,
      PREVIEW_COMPLETION_STYLE,
    );
    if (this.rectPreviewId) {
      this.addPreviewId(this.rectPreviewId);
    }
  }

  protected updatePreview(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.rectPreviewId) return;

    const bounds = this.getBounds();
    if (!bounds) return;

    // Update rectangle position and size
    overlay.update(this.rectPreviewId, {
      x: bounds.screenMin.x,
      y: bounds.screenMin.y,
      width: bounds.width,
      height: bounds.height,
    });
  }

  // ============== Zoom Action ==============

  protected createEntity(): void {
    const bounds = this.getBounds();
    if (!bounds) return;

    // Zoom to the window area
    this.viewer.zoomWindow(
      bounds.worldMin.x,
      bounds.worldMin.y,
      bounds.worldMax.x,
      bounds.worldMax.y,
    );

    this.viewer.update();

    // Call completion callback if set
    if (this.onZoomComplete) {
      this.onZoomComplete();
    }
  }

  protected clear(): void {
    super.clear();
    this.rectPreviewId = null;
  }
}
